import { useState, useEffect } from 'react';
import { flow } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { Switch, Route, Redirect } from 'react-router-dom';

import useAuth from '@giro/shared-hooks/useAuth.hook';
import useProtectedByAuth from '@giro-pdv/hooks/useProtectedByAuth.hook';
import useIsMobile from '@giro-pdv/hooks/useIsMobile.hook';

import AppLayout from '@giro-pdv/layouts/App.layout';

import ROUTES from '@giro-pdv/constants/routes.constant';

import DialogSendPaymentComponent from './components/DialogSendPayment.component';
import DialogPayComponent from './components/DialogPay.component';
import DialogPinpadComponent from './components/DialogPinpad.component';
import DialogPayTerminalComponent from './components/DialogPayTerminal.component';

import AgentScreen from './screens/Agent.screen';
import VehicleScreen from './screens/Vehicle.screen';
import GeralScreen from './screens/Geral.screen';
import AssessoriaScreen from './screens/Assessoria.screen';
import DetranScreen from './screens/Detran.screen';
import TotemScreen from './screens/Totem.screen';
import BilletScreen from './screens/Billet.screen';
import FinancingScreen from './screens/Financing.screen';
import MobileScreen from './screens/Mobile.screen';
import RechargeScreen from './screens/Recharge.screen';
import EducacaoScreen from './screens/Educacao.screen';
import CemiterioScreen from './screens/Cemiterio.screen';
import SimulationScreen from './screens/Simulation.screen';
import CartorioScreen from './screens/Cartorio.screen';
import CartorioBillingScreen from './screens/CartorioBilling.screen';
import AboutScreen from './screens/About.screen';
import LinkScreen from './screens/Link.screen';

import fees from '@giro-pdv/modules/pdv/store/fees';
import clients from '@giro-pdv/modules/pdv/store/clients';
import sync from './store/sync';
import tablePayment from './store/tablePayment';

import segmentNormalizeConstant from '@giro-pdv/constants/segmentNormalize.constant';

import websocketUtil from '@giro-pdv/utils/websocket.util';
import usePdvAccess from '@giro-pdv/hooks/usePdvAccess.hook';

const PDVModule = () => {
  const [connectSocket, setConnectSocket] = useState(false);

  const isMobile = useIsMobile();

  const { signedIn, user, token } = useAuth();

  const dispatch = useDispatch();

  const dispatchRedux = {
    feeServiceGet: flow(fees.action.serviceGet, dispatch),
    clientsServiceGet: flow(clients.action.serviceGet, dispatch),
    syncServicePost: flow(sync.action.servicePost, dispatch),
  };

  const clientsState = useSelector(clients.selector.selectState);
  const syncState = useSelector(sync.selector.selectState);

  // useEffect(() => {
  //   if (!signedIn) {
  //     return undefined;
  //   }

  //   window.onbeforeunload = function () {
  //     return '';
  //   };
  // }, [signedIn]);

  useEffect(() => {
    if (!signedIn) {
      return undefined;
    }

    dispatchRedux.feeServiceGet();
    dispatchRedux.clientsServiceGet();

    return () => {
      websocketUtil.disconnectWebSocket();
    };
  }, []);

  useEffect(() => {
    if (clientsState.loading !== false) {
      return undefined;
    }

    dispatchRedux.syncServicePost();
  }, [clientsState.loading]);

  useEffect(() => {
    if (syncState.loading !== false) {
      return undefined;
    }

    (async () => {
      await websocketUtil.connectWebSocket({
        onConnect: () => {
          setConnectSocket(true);
        },
      });
    })();
  }, [syncState.loading]);

  useEffect(() => {
    if (!connectSocket) {
      return undefined;
    }

    const linkedKey = syncState?.payload?.linkedkey;

    websocketUtil.syncCode(linkedKey, (data) => {
      dispatch(sync.action.updateLogSocket(data));
    });
  }, [connectSocket]);

  useEffect(() => {
    const lastLogSocket = syncState.logSocket[syncState.logSocket.length - 1];

    const lastStates = lastLogSocket?.states[lastLogSocket?.states.length - 1];

    if (lastStates?.event === 'pdv-terminal::request-client') {
      websocketUtil.addEvent(syncState?.payload?.linkedkey, {
        event: 'pdv::response-client',
        payload: {
          ...clientsState?.payload,
          token,
        },
      });
    }
  }, [syncState.logSocket]);

  useProtectedByAuth();

  const {
    hasSegment,
    rootRoute,
    hasBillingConfiguration,
    usesCartorioBilling,
  } = usePdvAccess();

  if (!signedIn) {
    return <Redirect to={ROUTES.PDV.ROOT} />;
  }

  const switchDesktop = (
    <Switch>
      <Route exact path={ROUTES.PDV.SIMULATION} component={SimulationScreen} />

      {hasSegment(segmentNormalizeConstant.DESPACHANTE) && (
        <Route exact path={ROUTES.PDV.AGENT} component={AgentScreen} />
      )}

      {hasSegment(segmentNormalizeConstant.VEICULOS) && (
        <Route exact path={ROUTES.PDV.VEHICLE} component={VehicleScreen} />
      )}

      {(hasSegment(segmentNormalizeConstant.GERAL) ||
        hasSegment(segmentNormalizeConstant.COOPNORE)) && (
        <Route exact path={ROUTES.PDV.GERAL} component={GeralScreen} />
      )}

      {hasSegment(segmentNormalizeConstant.CARTORIO) && (
        <Route
          exact
          path={ROUTES.PDV.CARTORIO}
          component={
            usesCartorioBilling ? CartorioBillingScreen : CartorioScreen
          }
        />
      )}

      {!usesCartorioBilling &&
        hasSegment(segmentNormalizeConstant.CARTORIO) && (
          <Route exact path={ROUTES.PDV.ABOUT} component={AboutScreen} />
        )}

      {hasSegment(segmentNormalizeConstant.CEMITERIO) && (
        <Route exact path={ROUTES.PDV.CEMITERIO} component={CemiterioScreen} />
      )}

      {hasSegment(segmentNormalizeConstant.EDUCACAO) && (
        <Route exact path={ROUTES.PDV.EDUCACAO} component={EducacaoScreen} />
      )}

      {hasSegment(segmentNormalizeConstant.PATIO) && (
        <Route exact path={ROUTES.PDV.VEHICLE} component={VehicleScreen} />
      )}

      {hasSegment(segmentNormalizeConstant.ACESSORIA) && (
        <Route
          exact
          path={ROUTES.PDV.ASSESSORIA}
          component={AssessoriaScreen}
        />
      )}

      {hasSegment(segmentNormalizeConstant.DETRAN) && (
        <Route exact path={ROUTES.PDV.DETRAN} component={DetranScreen} />
      )}

      {hasSegment(segmentNormalizeConstant.TOTEM) && (
        <Route exact path={ROUTES.PDV.TOTEM} component={TotemScreen} />
      )}

      {hasSegment(segmentNormalizeConstant.LINK_VAREJO) && (
        <Route exact path={ROUTES.PDV.LINK} component={LinkScreen} />
      )}

      {hasSegment(segmentNormalizeConstant.FINANCIAMENTO) && (
        <Route
          exact
          path={`${ROUTES.PDV.FINANCING}`}
          component={FinancingScreen}
        />
      )}

      {hasSegment(segmentNormalizeConstant.FINANCIAMENTO) && (
        <Route
          exact
          path={`${ROUTES.PDV.FINANCING}/:id`}
          component={FinancingScreen}
        />
      )}

      {hasSegment(segmentNormalizeConstant.BOLETO) && (
        <Route exact path={ROUTES.PDV.BILLET} component={BilletScreen} />
      )}

      {hasSegment(segmentNormalizeConstant.RECHARGE) && (
        <Route exact path={ROUTES.PDV.RECHARGE} component={RechargeScreen} />
      )}

      <Redirect to={rootRoute} />
    </Switch>
  );

  const switchMobile = (
    <Switch>
      <Route exact path={ROUTES.PDV.MOBILE} component={MobileScreen} />

      <Redirect to={ROUTES.PDV.MOBILE} />
    </Switch>
  );

  return (
    <AppLayout>
      {isMobile && !hasBillingConfiguration ? switchMobile : switchDesktop}

      <DialogSendPaymentComponent />
      <DialogPayComponent />
      <DialogPayTerminalComponent />
    </AppLayout>
  );
};

export default PDVModule;
